import { useState, useRef, useMemo } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";

export interface BikeFieldComboboxProps {
  options: string[];
  pendingOptions?: string[];
  value: string;
  onChange: (value: string) => void;
  onSuggest: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  className?: string;
}

export function BikeFieldCombobox({
  options,
  pendingOptions = [],
  value,
  onChange,
  onSuggest,
  placeholder = "Search...",
  disabled = false,
  isLoading = false,
  className,
}: BikeFieldComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, query]);

  const filteredPending = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return pendingOptions;
    return pendingOptions.filter((o) => o.toLowerCase().includes(q));
  }, [pendingOptions, query]);

  const queryMatchesPending = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q.length > 0 && pendingOptions.some((o) => o.toLowerCase() === q);
  }, [pendingOptions, query]);

  const handleSelect = (option: string) => {
    onChange(option);
    setQuery("");
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
    setQuery("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
    }
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      setQuery("");
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const showSuggest = query.trim().length > 0 && filtered.length === 0 && !queryMatchesPending;

  if (isLoading) {
    return <div className={cn("h-9 animate-pulse rounded-md border bg-muted", className)} />;
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={false}>
      <PopoverTrigger asChild>
        <div
          role="combobox"
          aria-expanded={open}
          className={cn(
            "flex h-9 w-full cursor-default items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm ring-offset-background max-md:text-base!",
            "focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
            disabled && "cursor-not-allowed opacity-50",
            className,
          )}
        >
          {value && !open ? (
            <>
              <span className="flex-1 truncate">{value}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label="Clear selection"
                >
                  <X className="size-4" />
                </button>
              )}
            </>
          ) : (
            <>
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={open ? "Type to search..." : value || placeholder}
                disabled={disabled}
                className="flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
              />
              <ChevronDown className="pointer-events-none size-4 shrink-0 opacity-50" />
            </>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div
          className="max-h-[240px] overflow-y-auto overscroll-contain p-1"
          onWheel={(e) => e.stopPropagation()}
        >
          {filtered.map((option) => (
            <button
              key={option}
              type="button"
              className={cn(
                "flex w-full cursor-default items-center rounded-sm px-2 py-1.5 text-sm",
                "hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground outline-none",
              )}
              onClick={() => handleSelect(option)}
            >
              {option}
            </button>
          ))}
          {filteredPending.map((option) => (
            <div
              key={`pending-${option}`}
              className="flex w-full cursor-default items-center justify-between rounded-sm px-2 py-1.5 text-sm text-muted-foreground"
            >
              <span>{option}</span>
              <span className="ml-2 shrink-0 text-xs italic">pending approval</span>
            </div>
          ))}
          {showSuggest && (
            <button
              type="button"
              className={cn(
                "flex w-full cursor-default items-center rounded-sm px-2 py-1.5 text-sm",
                "hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground outline-none",
                "text-muted-foreground italic",
              )}
              onClick={() => {
                onSuggest(query.trim());
                setQuery("");
                setOpen(false);
              }}
            >
              Suggest &ldquo;{query.trim()}&rdquo; for approval
            </button>
          )}
          {queryMatchesPending && (
            <div className="px-2 py-1.5 text-sm text-muted-foreground italic">
              &ldquo;{query.trim()}&rdquo; is already pending approval
            </div>
          )}
          {filtered.length === 0 &&
            filteredPending.length === 0 &&
            !showSuggest &&
            !queryMatchesPending && (
              <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                No options available
              </div>
            )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
